import { API_BASE_URL } from '../config/api';

// Default API configuration
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

const TOKEN_KEY = 'authToken';

// Helper function to get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return {
    ...DEFAULT_HEADERS,
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Generic API call function
export const apiCall = async (endpoint, options = {}) => {
  let url = `${API_BASE_URL}${endpoint}`;
  
  // Handle query parameters for GET requests
  if (options.params && Object.keys(options.params).length > 0) {
    const queryString = new URLSearchParams(options.params).toString();
    url += `?${queryString}`;
  }
  
  // Check if this is a public endpoint (should not include auth token)
  const isPublicEndpoint = endpoint.includes('/public/') || options.skipAuth === true;
  
  // Get headers - skip auth for public endpoints
  const headers = isPublicEndpoint 
    ? { ...DEFAULT_HEADERS }
    : getAuthHeaders();
  
  const config = {
    headers: {
      ...headers,
      ...(options.headers || {})
    },
    ...options
  };
  
  // Remove skipAuth from config as it's not a fetch option
  delete config.skipAuth;

  // Remove params from config as they're now in the URL
  delete config.params;

  // Handle body data for POST/PUT/PATCH requests
  if (options.data && ['POST', 'PUT', 'PATCH'].includes(options.method)) {
    config.body = JSON.stringify(options.data);
    delete config.data; // Remove data from config as it's now in body
  }

  // Log API call details for PUT requests (updates)
  if (options.method === 'PUT') {
    console.log('🌐 Full API URL:', url);
    console.log('📤 Request Method:', options.method);
    console.log('📦 Request Body:', config.body);
  }

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
    }

    return { success: true, data };
  } catch (error) {
    console.error(`API call to ${endpoint} failed:`, error);
    return { 
      success: false, 
      error: error.message || 'An error occurred during the API call' 
    };
  }
};

// Specific API functions
export const loginUser = async (email, password) => {
  return apiCall('/login', {
    method: 'POST',
    data: { email, password }
  });
};

export const logoutUser = async () => {
  return apiCall('/logout', {
    method: 'POST'
  });
};

export const getUserProfile = async () => {
  return apiCall('/profile');
};

export const updateUserProfile = async (userData) => {
  return apiCall('/profile', {
    method: 'PUT',
    data: userData
  });
};

export const changePassword = async (currentPassword, newPassword) => {
  return apiCall('/change-password', {
    method: 'POST',
    data: { 
      current_password: currentPassword, 
      new_password: newPassword 
    }
  });
};

export const forgotPassword = async (email) => {
  return apiCall('/forgot-password', {
    method: 'POST',
    data: { email }
  });
};

export const resetPassword = async (token, newPassword, confirmPassword) => {
  return apiCall('/reset-password', {
    method: 'POST',
    data: { 
      token, 
      password: newPassword,
      password_confirmation: confirmPassword 
    }
  });
};
